import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2, ArrowLeft, FileDown, Phone, Mail } from "lucide-react";
import SEOHead from "@/components/layout/SEOHead";
import { PageHero } from "@/pages/About.hero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEMBERSHIP_TYPES = [
  "Family - Two Adult Household",
  "Family - Single Parent Household",
  "Adult - Couple (36-64, no children)",
  "Adult - Single (36-64, no children)",
  "Young Adult - Couple/family (under 36, kids under K)",
  "Young Adult - Individual (under 36)",
  "Senior - Couple (65+)",
  "Senior - Single (65+)",
  "Please consider financial alternatives",
];

const RELIGIOUS_TRADITIONS = ["Reform", "Orthodox", "Conservative", "Non-Jewish"];
const RELIGIOUS_STATUSES = ["Born Jewish", "Non-Jewish", "Jew-by-conversion"];
const MARITAL_STATUSES = ["Single", "Married", "Partnered", "Widowed", "Divorced"];
const COMMITTEES = [
  "Development", "Caring", "Finance", "Marketing", "House",
  "Education", "Engagement", "Ritual", "Membership", "Social Action",
];
const CONTACT_PREFS = ["Email", "Phone", "Text"];

const EMPTY_ADULT = {
  name: "", email: "", cell_phone: "", home_phone: "",
  birthday: "", anniversary: "", occupation: "", marital_status: "",
  religious_tradition: "", religious_status: "",
  directory_ok: true, hebrew_name: "",
};
const EMPTY_CHILD = { name: "", birthday: "", hebrew_name: "" };
const EMPTY_YAHRZEIT = {
  first_name: "", last_name: "", related_to: "Adult 1",
  relationship: "", date_of_death: "", hebrew_name: "",
};

/**
 * Reusable membership application form + paper-form download block.
 * Renders inline (no hero) so it can be embedded on the /membership page.
 * When submitted, replaces itself with a success confirmation.
 */
export function MembershipApplicationForm({ compact = false }) {
  const [pdfExists, setPdfExists] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    household_name: "",
    membership_type: "Family - Two Adult Household",
    primary_email: "",
    primary_phone: "",
    address: "",
    city: "",
    state: "FL",
    zip_code: "",
    heard_from: "",
    interests: "",
    questions: "",
    adults: [{ ...EMPTY_ADULT }],
    children: [],
    yahrzeits: [],
    committees: [],
    contact_prefs: ["Email"],
    photo_release_opt_out: false,
    typed_signature: "",
  });

  useEffect(() => {
    // The React SPA returns HTML for unknown paths. Treat the PDF as present
    // whenever the response is OK and NOT text/html - accepts both
    // application/pdf and application/octet-stream headers from the CDN.
    fetch("/membership-application.pdf", { method: "HEAD" })
      .then((r) => {
        const ct = (r.headers.get("content-type") || "").toLowerCase();
        setPdfExists(r.ok && !ct.startsWith("text/"));
      })
      .catch(() => setPdfExists(false));
  }, []);

  const setAdult = (i, k, v) => {
    const adults = [...form.adults];
    adults[i] = { ...adults[i], [k]: v };
    setForm({ ...form, adults });
  };
  const setChild = (i, k, v) => {
    const children = [...form.children];
    children[i] = { ...children[i], [k]: v };
    setForm({ ...form, children });
  };
  const addAdult = () => setForm({ ...form, adults: [...form.adults, { ...EMPTY_ADULT }] });
  const removeAdult = (i) =>
    setForm({ ...form, adults: form.adults.filter((_, x) => x !== i) });
  const addChild = () => setForm({ ...form, children: [...form.children, { ...EMPTY_CHILD }] });
  const removeChild = (i) =>
    setForm({ ...form, children: form.children.filter((_, x) => x !== i) });

  const setYahrzeit = (i, k, v) => {
    const yahrzeits = [...form.yahrzeits];
    yahrzeits[i] = { ...yahrzeits[i], [k]: v };
    setForm({ ...form, yahrzeits });
  };
  const addYahrzeit = () => setForm({ ...form, yahrzeits: [...form.yahrzeits, { ...EMPTY_YAHRZEIT }] });
  const removeYahrzeit = (i) =>
    setForm({ ...form, yahrzeits: form.yahrzeits.filter((_, x) => x !== i) });

  const toggle = (key, val) => {
    const arr = form[key] || [];
    setForm({ ...form, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const firstBlank = [...e.currentTarget.querySelectorAll("[required]")].find(
      (el) => !String(el.value || "").trim()
    );
    if (firstBlank) {
      firstBlank.focus();
      toast.error("Please complete all required fields.");
      return;
    }
    if (!form.contact_prefs.length) {
      toast.error("Please choose at least one way for us to reach you.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/membership/apply`, form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sorry - something went wrong. Please email tioffice@tiofbrevard.com.");
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto text-center py-8" data-testid="membership-apply-success">
        <CheckCircle2 className="w-16 h-16 text-[color:var(--honey)] mx-auto mb-4" />
        <h3 className="font-display text-2xl text-[color:var(--blue-ink)] mb-2">Todah rabah!</h3>
        <p className="text-lg text-[color:var(--ink)] mb-6">
          Your application is in Terry&rsquo;s inbox. One of us will be in touch within
          2-3 business days to welcome you.
        </p>
        <div className="grid sm:grid-cols-2 gap-4 text-left mb-6">
          <div className="card-soft p-5">
            <p className="eyebrow mb-2">★ Questions?</p>
            <p className="font-semibold text-[color:var(--blue-ink)]">Barb Cannon</p>
            <p className="text-sm text-[color:var(--ink-soft)] mt-1">
              <a href="tel:5852819222" className="hover:underline">
                <Phone className="inline w-3 h-3 mr-1" /> 585-281-9222
              </a>
            </p>
            <p className="text-sm text-[color:var(--ink-soft)]">
              <a href="mailto:BLCannon527@gmail.com" className="hover:underline">
                <Mail className="inline w-3 h-3 mr-1" /> BLCannon527@gmail.com
              </a>
            </p>
          </div>
          <div className="card-soft p-5">
            <p className="eyebrow mb-2">★ Temple Office</p>
            <p className="font-semibold text-[color:var(--blue-ink)]">Terry Ginsburg</p>
            <p className="text-sm text-[color:var(--ink-soft)] mt-1">
              <a href="mailto:tioffice@tiofbrevard.com" className="hover:underline">
                <Mail className="inline w-3 h-3 mr-1" /> tioffice@tiofbrevard.com
              </a>
            </p>
            <p className="text-sm text-[color:var(--ink-soft)]">
              <a href="tel:3216319494" className="hover:underline">
                <Phone className="inline w-3 h-3 mr-1" /> (321) 631-9494
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full" data-testid="membership-application-form">
      {/* Prefer paper? */}
      <div className="rounded-2xl bg-[color:var(--cream)] p-5 md:p-6 mb-8 flex flex-wrap items-center gap-4 justify-between" data-testid="membership-paper-block">
        <div>
          <p className="font-semibold text-[color:var(--blue-ink)]">
            Prefer to fill out a paper form?
          </p>
          <p className="text-sm text-[color:var(--ink-soft)] mt-1">
            {pdfExists
              ? "Download the fillable PDF, complete it, and email it back to tioffice@tiofbrevard.com."
              : "Contact Terry at the temple office and she'll email you the PDF."}
          </p>
        </div>
        {pdfExists ? (
          <a
            href="/membership-application.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost"
            data-testid="membership-pdf-download"
          >
            <FileDown className="w-4 h-4" /> Download PDF
          </a>
        ) : (
          <a href="mailto:tioffice@tiofbrevard.com?subject=Please%20send%20me%20the%20membership%20application%20PDF" className="btn-ghost">
            <Mail className="w-4 h-4" /> Email the office
          </a>
        )}
      </div>

      <form onSubmit={submit} className="space-y-10">
        <FieldGroup title="About your household">
          <label className="block">
            <span className="lbl">Family / household name *</span>
            <input required type="text" className="inp"
              placeholder="e.g., The Fanwick Family"
              value={form.household_name}
              onChange={(e) => setForm({ ...form, household_name: e.target.value })}
              data-testid="apply-household" />
          </label>
          <label className="block">
            <span className="lbl">Which best describes what you&rsquo;re looking for? *</span>
            <select required className="inp"
              value={form.membership_type}
              onChange={(e) => setForm({ ...form, membership_type: e.target.value })}
              data-testid="apply-type">
              {MEMBERSHIP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <span className="hint">Dues are pledged after we connect - no need to select an amount here.</span>
          </label>
        </FieldGroup>

        <FieldGroup title="Primary contact">
          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="lbl">Best email address *</span>
              <input required type="email" className="inp"
                value={form.primary_email}
                onChange={(e) => setForm({ ...form, primary_email: e.target.value })}
                data-testid="apply-email" />
            </label>
            <label className="block">
              <span className="lbl">Best phone number *</span>
              <input required type="tel" className="inp"
                value={form.primary_phone}
                onChange={(e) => setForm({ ...form, primary_phone: e.target.value })}
                data-testid="apply-phone" />
            </label>
          </div>
          <label className="block">
            <span className="lbl">Street address *</span>
            <input required type="text" className="inp"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              data-testid="apply-address" />
          </label>
          <div className="grid md:grid-cols-3 gap-4">
            <label className="block">
              <span className="lbl">City *</span>
              <input required type="text" className="inp"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                data-testid="apply-city" />
            </label>
            <label className="block">
              <span className="lbl">State *</span>
              <input required type="text" className="inp" maxLength={2}
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
                data-testid="apply-state" />
            </label>
            <label className="block">
              <span className="lbl">ZIP *</span>
              <input required type="text" className="inp" maxLength={10}
                value={form.zip_code}
                onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                data-testid="apply-zip" />
            </label>
          </div>
        </FieldGroup>

        <FieldGroup title="Adults in the household" hint="At least one required. Fields marked * are required.">
          {form.adults.map((a, i) => (
            <div key={i} className="rounded-xl border border-[color:var(--line)] p-4 relative">
              {i > 0 && (
                <button type="button"
                  onClick={() => removeAdult(i)}
                  className="absolute top-3 right-3 text-red-600 hover:bg-red-50 p-1.5 rounded-full"
                  aria-label="Remove adult">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
              <p className="text-xs uppercase tracking-wider text-[color:var(--muted)] font-semibold mb-3">
                {i === 0 ? "Primary adult" : `Adult ${i + 1}`}
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                <label className="block">
                  <span className="lbl">Full name *</span>
                  <input required type="text" className="inp"
                    value={a.name} onChange={(e) => setAdult(i, "name", e.target.value)}
                    data-testid={`apply-adult-name-${i}`} />
                </label>
                <label className="block">
                  <span className="lbl">Hebrew name (if any)</span>
                  <input type="text" className="inp"
                    value={a.hebrew_name} onChange={(e) => setAdult(i, "hebrew_name", e.target.value)} />
                </label>
                <label className="block">
                  <span className="lbl">Email *</span>
                  <input required type="email" className="inp"
                    value={a.email} onChange={(e) => setAdult(i, "email", e.target.value)}
                    data-testid={`apply-adult-email-${i}`} />
                </label>
                <label className="block">
                  <span className="lbl">Cell phone *</span>
                  <input required type="tel" className="inp"
                    value={a.cell_phone} onChange={(e) => setAdult(i, "cell_phone", e.target.value)}
                    data-testid={`apply-adult-cell-${i}`} />
                </label>
                {i === 0 && (
                  <label className="block">
                    <span className="lbl">Home phone</span>
                    <input type="tel" className="inp"
                      value={a.home_phone} onChange={(e) => setAdult(i, "home_phone", e.target.value)} />
                  </label>
                )}
                <label className="block">
                  <span className="lbl">Birthday *</span>
                  <input required type="text" placeholder="MM/DD/YYYY" className="inp"
                    value={a.birthday} onChange={(e) => setAdult(i, "birthday", e.target.value)}
                    data-testid={`apply-adult-birthday-${i}`} />
                </label>
                <label className="block">
                  <span className="lbl">Anniversary (if applicable)</span>
                  <input type="text" placeholder="MM/DD/YYYY" className="inp"
                    value={a.anniversary} onChange={(e) => setAdult(i, "anniversary", e.target.value)} />
                </label>
                <label className="block">
                  <span className="lbl">Occupation *</span>
                  <input required type="text" className="inp"
                    value={a.occupation} onChange={(e) => setAdult(i, "occupation", e.target.value)}
                    data-testid={`apply-adult-occupation-${i}`} />
                </label>
                <label className="block">
                  <span className="lbl">Marital status *</span>
                  <select required className="inp" value={a.marital_status}
                    onChange={(e) => setAdult(i, "marital_status", e.target.value)}
                    data-testid={`apply-adult-marital-${i}`}>
                    <option value="">-- select --</option>
                    {MARITAL_STATUSES.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="lbl">Religious tradition *</span>
                  <select required className="inp" value={a.religious_tradition}
                    onChange={(e) => setAdult(i, "religious_tradition", e.target.value)}
                    data-testid={`apply-adult-tradition-${i}`}>
                    <option value="">-- select --</option>
                    {RELIGIOUS_TRADITIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="lbl">Current religious status *</span>
                  <select required className="inp" value={a.religious_status}
                    onChange={(e) => setAdult(i, "religious_status", e.target.value)}
                    data-testid={`apply-adult-status-${i}`}>
                    <option value="">-- select --</option>
                    {RELIGIOUS_STATUSES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </label>
                <label className="inline-flex items-center gap-2 md:col-span-2 text-sm mt-1">
                  <input type="checkbox" checked={a.directory_ok}
                    onChange={(e) => setAdult(i, "directory_ok", e.target.checked)} />
                  Include my name, phone, and email in the temple directory
                </label>
              </div>
            </div>
          ))}
          <button type="button" onClick={addAdult}
            className="btn-ghost text-sm" data-testid="apply-add-adult">
            <Plus className="w-4 h-4" /> Add another adult
          </button>
        </FieldGroup>

        <FieldGroup title="Children (if any)" hint="Names, ages - as many as you'd like">
          {form.children.length === 0 && (
            <p className="text-sm text-[color:var(--ink-soft)]">No children added yet.</p>
          )}
          {form.children.map((c, i) => (
            <div key={i} className="rounded-xl border border-[color:var(--line)] p-4 relative">
              <button type="button"
                onClick={() => removeChild(i)}
                className="absolute top-3 right-3 text-red-600 hover:bg-red-50 p-1.5 rounded-full"
                aria-label="Remove child">
                <Trash2 className="w-4 h-4" />
              </button>
              <div className="grid md:grid-cols-3 gap-3">
                <label className="block md:col-span-2">
                  <span className="lbl">Full name *</span>
                  <input required type="text" className="inp"
                    value={c.name} onChange={(e) => setChild(i, "name", e.target.value)}
                    data-testid={`apply-child-name-${i}`} />
                </label>
                <label className="block">
                  <span className="lbl">Birthday *</span>
                  <input required type="text" placeholder="MM/DD/YYYY" className="inp"
                    value={c.birthday} onChange={(e) => setChild(i, "birthday", e.target.value)}
                    data-testid={`apply-child-birthday-${i}`} />
                </label>
                <label className="block md:col-span-3">
                  <span className="lbl">Hebrew name (if any)</span>
                  <input type="text" className="inp"
                    value={c.hebrew_name} onChange={(e) => setChild(i, "hebrew_name", e.target.value)} />
                </label>
              </div>
            </div>
          ))}
          <button type="button" onClick={addChild}
            className="btn-ghost text-sm" data-testid="apply-add-child">
            <Plus className="w-4 h-4" /> Add a child
          </button>
        </FieldGroup>

        {!compact && (
          <FieldGroup title="Yahrzeit remembrance (optional)" hint="Names of loved ones you'd like remembered each year">
            {form.yahrzeits.length === 0 && (
              <p className="text-sm text-[color:var(--ink-soft)]">No Yahrzeit entries yet.</p>
            )}
            {form.yahrzeits.map((y, i) => (
              <div key={i} className="rounded-xl border border-[color:var(--line)] p-4 relative">
                <button type="button"
                  onClick={() => removeYahrzeit(i)}
                  className="absolute top-3 right-3 text-red-600 hover:bg-red-50 p-1.5 rounded-full"
                  aria-label="Remove entry">
                  <Trash2 className="w-4 h-4" />
                </button>
                <div className="grid md:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="lbl">First name *</span>
                    <input required type="text" className="inp"
                      value={y.first_name} onChange={(e) => setYahrzeit(i, "first_name", e.target.value)}
                      data-testid={`apply-yahrzeit-first-${i}`} />
                  </label>
                  <label className="block">
                    <span className="lbl">Last name *</span>
                    <input required type="text" className="inp"
                      value={y.last_name} onChange={(e) => setYahrzeit(i, "last_name", e.target.value)}
                      data-testid={`apply-yahrzeit-last-${i}`} />
                  </label>
                  <label className="block">
                    <span className="lbl">Hebrew name (if any)</span>
                    <input type="text" className="inp"
                      value={y.hebrew_name} onChange={(e) => setYahrzeit(i, "hebrew_name", e.target.value)} />
                  </label>
                  <label className="block">
                    <span className="lbl">Related to *</span>
                    <select required className="inp" value={y.related_to}
                      onChange={(e) => setYahrzeit(i, "related_to", e.target.value)}
                      data-testid={`apply-yahrzeit-related-${i}`}>
                      <option>Adult 1</option>
                      <option>Adult 2</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="lbl">Relationship (parent, aunt, friend...) *</span>
                    <input required type="text" className="inp"
                      value={y.relationship} onChange={(e) => setYahrzeit(i, "relationship", e.target.value)}
                      data-testid={`apply-yahrzeit-relationship-${i}`} />
                  </label>
                  <label className="block">
                    <span className="lbl">Date of passing *</span>
                    <input required type="text" placeholder="MM/DD/YYYY" className="inp"
                      value={y.date_of_death} onChange={(e) => setYahrzeit(i, "date_of_death", e.target.value)}
                      data-testid={`apply-yahrzeit-date-${i}`} />
                  </label>
                </div>
              </div>
            ))}
            <button type="button" onClick={addYahrzeit} className="btn-ghost text-sm" data-testid="apply-add-yahrzeit">
              <Plus className="w-4 h-4" /> Add Yahrzeit
            </button>
          </FieldGroup>
        )}

        {!compact && (
          <FieldGroup title="Membership category">
            <p className="text-sm text-[color:var(--ink-soft)] mb-2">
              Pick the category that fits best. If you&rsquo;d like to discuss financial alternatives,
              select the last option and we&rsquo;ll follow up privately.
            </p>
            <div className="space-y-2">
              {MEMBERSHIP_TYPES.map((t) => (
                <label key={t} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="membership_type" value={t}
                    checked={form.membership_type === t}
                    onChange={() => setForm({ ...form, membership_type: t })} />
                  {t}
                </label>
              ))}
            </div>
          </FieldGroup>
        )}

        {!compact && (
          <FieldGroup title="Where would you like to help?" hint="Check any committees you're interested in - no commitment, we'll follow up.">
            <div className="grid grid-cols-2 gap-2">
              {COMMITTEES.map((c) => (
                <label key={c} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox"
                    checked={form.committees.includes(c)}
                    onChange={() => toggle("committees", c)}
                    data-testid={`apply-committee-${c.toLowerCase().replace(/\s+/g, "-")}`} />
                  {c}
                </label>
              ))}
            </div>
          </FieldGroup>
        )}

        {!compact && (
          <FieldGroup title="How should we reach you? *" hint="Choose at least one">
            <div className="flex flex-wrap gap-4">
              {CONTACT_PREFS.map((p) => (
                <label key={p} className="inline-flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox"
                    checked={form.contact_prefs.includes(p)}
                    onChange={() => toggle("contact_prefs", p)}
                    data-testid={`apply-contactpref-${p.toLowerCase()}`} />
                  {p}
                </label>
              ))}
            </div>
          </FieldGroup>
        )}

        {!compact && (
          <FieldGroup title="Photo release & signature">
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="checkbox" className="mt-1"
                checked={form.photo_release_opt_out}
                onChange={(e) => setForm({ ...form, photo_release_opt_out: e.target.checked })}
                data-testid="apply-photo-optout" />
              <span>Check this box if you do <strong>NOT</strong> consent to being photographed at temple events or having your image used in temple communications.</span>
            </label>
            <label className="block">
              <span className="lbl">Typed signature (your full name serves as signature) *</span>
              <input required type="text" className="inp"
                placeholder="Full legal name"
                value={form.typed_signature}
                onChange={(e) => setForm({ ...form, typed_signature: e.target.value })}
                data-testid="apply-signature" />
            </label>
          </FieldGroup>
        )}

        {!compact && (
          <FieldGroup title="A few more things (optional)">
            <label className="block">
              <span className="lbl">How did you hear about us?</span>
              <input type="text" className="inp"
                value={form.heard_from}
                onChange={(e) => setForm({ ...form, heard_from: e.target.value })} />
            </label>
            <label className="block">
              <span className="lbl">What are you hoping to find at TIOB?</span>
              <textarea rows={3} className="inp"
                placeholder="Religious school for our kids, community, learning, etc."
                value={form.interests}
                onChange={(e) => setForm({ ...form, interests: e.target.value })} />
            </label>
            <label className="block">
              <span className="lbl">Questions for us?</span>
              <textarea rows={3} className="inp"
                value={form.questions}
                onChange={(e) => setForm({ ...form, questions: e.target.value })} />
            </label>
          </FieldGroup>
        )}

        <div className="pt-2">
          <button type="submit" disabled={busy}
            className="btn-primary w-full sm:w-auto justify-center text-base"
            data-testid="apply-submit">
            {busy ? "Sending…" : "Send my application"}
          </button>
          <p className="text-xs text-[color:var(--ink-soft)] mt-3">
            Your application will be sent to the Membership Committee, who will follow
            up personally within a few business days.
          </p>
        </div>
      </form>
    </div>
  );
}

/** Full-page /membership/apply route (kept for direct linking). */
export default function MembershipApply() {
  return (
    <>
      <SEOHead
        title="Become a Member - Application"
        description="Apply to join Temple Israel of Brevard. Complete the online membership application and we'll be in touch within 2-3 business days."
        path="/membership/apply"
      />
      <PageHero
        eyebrow="Become a Member"
        title="Join our synagogue family."
        subtitle="A short application - one of us follows up within a few business days to welcome you and answer any questions."
      />
      <section className="bg-[#EAF3FB] py-12 lg:py-16">
        <div className="max-w-3xl mx-auto px-5 lg:px-10">
          <MembershipApplicationForm />
          <div className="mt-10 text-center">
            <Link to="/membership" className="btn-ghost text-sm">
              <ArrowLeft className="w-4 h-4" /> Back to Membership
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function FieldGroup({ title, hint, children }) {
  return (
    <section className="space-y-4">
      <header>
        <h2 className="font-display text-xl text-[color:var(--blue-ink)]">{title}</h2>
        {hint && <p className="text-xs text-[color:var(--ink-soft)] mt-1">{hint}</p>}
      </header>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

